import Component from '@glimmer/component';
import { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import WhosViewingComponent from './whos-viewing.gts';
import type AppearanceService from '../../services/appearance.ts';
import type { Appearance } from '../../services/appearance.ts';
import type Owner from '@ember/owner';

interface S {
  Args: {
    apiKey: string;
    apiHost: string;
    token?: string;
    type: string;
    documentId: string;
  };
  Element: HTMLDivElement;
}

export default class AppearanceComponent extends Component<S> {
  @service declare appearance: AppearanceService;

  @tracked appearances: Appearance[] = [];

  private document: { type: string; documentId: string } | null = null;

  constructor(owner: Owner, args: S['Args']) {
    super(owner, args);

    this.appearance.connect(this.args.apiHost, this.args.apiKey, this.args.token || '');
    document.addEventListener('visibilitychange', this.update);

    registerDestructor(this, () => {
      document.removeEventListener('visibilitychange', this.update);
    });
  }

  // Re-runs when the document changes, tearing down the previous channel first,
  // so a page that swaps records in place never keeps publishing to the old one.
  presence = modifier((_element: HTMLElement, [type, documentId]: [string, string]) => {
    this.document = { type, documentId };
    this.appear();

    return () => {
      this.away();
      this.document = null;
      this.appearances = [];
    };
  });

  get documentIsActive() {
    return document.visibilityState === 'visible';
  }

  appear() {
    if (!this.document) return;
    const { type, documentId } = this.document;
    this.appearance.subscribe(type, documentId, (message: Record<string, Appearance>) => {
      this.appearances = Object.values(message);
    });
  }

  away() {
    if (!this.document) return;
    this.appearance.unsubscribe(this.document.type, this.document.documentId);
  }

  update = () => {
    if (this.documentIsActive) {
      this.appear();
    } else {
      this.away();
    }
  };

  <template>
    <div {{this.presence @type @documentId}} ...attributes>
      {{#if this.appearances.length}}
        <WhosViewingComponent @appearances={{this.appearances}} />
      {{/if}}
    </div>
  </template>
}
